'use client';

import { createClient } from '@/lib/supabase/client';

export interface ListingRow {
  id: string;
  title: string;
  description: string | null;
  price: number | null;
  address: string | null;
  city: string | null;
  state: string | null;
  locality: string | null;
  district: string | null;
  latitude: number | null;
  longitude: number | null;
  photos_json: string[] | null;
  specs_json: Record<string, any> | null;
  amenities_json: string[] | null;
  property_size: string | null;
  owner_name: string | null;
  owner_mobile: string | null;
  owner_whatsapp: string | null;
  is_verified: boolean;
  is_featured: boolean;
  listing_status: string;
  approval_status: string;
  view_count: number;
  lead_count: number;
  khata_status: string | null;
  ec_available: boolean;
  loan_eligible: boolean;
  created_at: string;
  updated_at: string;
  category_id: string | null;
  categories?: { name: string; slug: string; icon: string | null; color: string | null } | null;
  user_profiles?: { full_name: string; avatar_url: string | null; mobile: string | null; is_verified: boolean } | null;
}

function formatPrice(price: number | null): string {
  if (!price) return 'Price on Request';
  if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
  if (price >= 100000) return `₹${(price / 100000).toFixed(0)} Lakhs`;
  if (price >= 1000) return `₹${(price / 1000).toFixed(0)}K`;
  return `₹${price}`;
}

function getListingLocation(listing: ListingRow): string {
  const parts = [listing.locality, listing.city].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : listing.address || 'Bengaluru';
}

function getFirstPhoto(listing: ListingRow): string {
  const photos = listing.photos_json;
  if (Array.isArray(photos) && photos.length > 0) {
    const first = photos[0];
    if (typeof first === 'string' && first.startsWith('http')) return first;
    if (typeof first === 'object' && (first as any).url) return (first as any).url;
  }
  return 'https://images.unsplash.com/photo-1700682274971-a0f29a99d8b9?w=800&q=80';
}

function getAllPhotos(listing: ListingRow): Array<{ src: string; alt: string }> {
  const photos = listing.photos_json;
  if (Array.isArray(photos) && photos.length > 0) {
    return photos.map((p, i) => {
      const src = typeof p === 'string' ? p : (p as any).url || '';
      return { src: src.startsWith('http') ? src : 'https://images.unsplash.com/photo-1700682274971-a0f29a99d8b9?w=800&q=80', alt: `${listing.title} - Photo ${i + 1}` };
    });
  }
  return [
    { src: 'https://images.unsplash.com/photo-1700682274971-a0f29a99d8b9?w=800&q=80', alt: listing.title },
  ];
}

function getListingType(listing: ListingRow): string {
  const catName = listing.categories?.name;
  if (catName) return catName;
  const specs = listing.specs_json || {};
  return specs.property_type || specs.listing_type || 'Property';
}

function getListingStatus(listing: ListingRow): 'For Sale' | 'For Rent' {
  const specs = listing.specs_json || {};
  const listingType = (specs.listing_type || specs.transaction_type || '').toLowerCase();
  if (listingType.includes('rent') || listingType.includes('lease')) return 'For Rent';
  return 'For Sale';
}

export function mapToFeaturedListing(listing: ListingRow) {
  const specs = listing.specs_json || {};
  return {
    id: listing.id,
    title: listing.title,
    price: formatPrice(listing.price),
    pricePerSqft: specs.price_per_sqft ? `₹${specs.price_per_sqft}/sqft` : undefined,
    location: getListingLocation(listing),
    beds: Number(specs.bedrooms || specs.beds || 0),
    baths: Number(specs.bathrooms || specs.baths || 0),
    area: listing.property_size || specs.area || specs.carpet_area || '',
    type: getListingType(listing),
    status: getListingStatus(listing),
    isFeatured: listing.is_featured,
    isVerified: listing.is_verified,
    image: getFirstPhoto(listing),
    imageAlt: listing.title,
    seller: listing.owner_name || listing.user_profiles?.full_name || 'Owner',
    sellerType: specs.seller_type || 'Individual',
  };
}

export function mapToListingCard(listing: ListingRow) {
  const specs = listing.specs_json || {};
  return {
    id: listing.id,
    title: listing.title,
    price: formatPrice(listing.price),
    priceRaw: listing.price || 0,
    pricePerSqft: specs.price_per_sqft ? `₹${specs.price_per_sqft}/sqft` : undefined,
    location: getListingLocation(listing),
    area: listing.property_size || specs.area || specs.carpet_area || '',
    beds: Number(specs.bedrooms || specs.beds || 0),
    baths: Number(specs.bathrooms || specs.baths || 0),
    type: getListingType(listing),
    status: getListingStatus(listing),
    badge: listing.is_featured ? 'Featured' : undefined,
    image: getFirstPhoto(listing),
    imageAlt: listing.title,
    seller: listing.owner_name || listing.user_profiles?.full_name || 'Owner',
    sellerType: specs.seller_type || 'Individual',
    date: new Date(listing.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
    isVerified: listing.is_verified,
    isFeatured: listing.is_featured,
    lat: listing.latitude ? Number(listing.latitude) : undefined,
    lng: listing.longitude ? Number(listing.longitude) : undefined,
  };
}

export function mapToDetailListing(listing: ListingRow) {
  const specs = listing.specs_json || {};
  const amenities = Array.isArray(listing.amenities_json) ? listing.amenities_json : [];
  return {
    id: listing.id,
    title: listing.title,
    description: listing.description || '',
    price: formatPrice(listing.price),
    pricePerSqft: specs.price_per_sqft ? `₹${specs.price_per_sqft} per sqft` : null,
    location: getListingLocation(listing),
    fullAddress: [listing.address, listing.locality, listing.city, listing.state, listing.district].filter(Boolean).join(', '),
    city: listing.city || 'Bengaluru',
    state: listing.state || 'Karnataka',
    pincode: '',
    latitude: listing.latitude ? Number(listing.latitude) : null,
    longitude: listing.longitude ? Number(listing.longitude) : null,
    photos: getAllPhotos(listing),
    type: getListingType(listing),
    status: getListingStatus(listing),
    isFeatured: listing.is_featured,
    isVerified: listing.is_verified,
    viewCount: listing.view_count || 0,
    beds: Number(specs.bedrooms || specs.beds || 0),
    baths: Number(specs.bathrooms || specs.baths || 0),
    area: listing.property_size || specs.area || specs.carpet_area || '',
    facing: specs.facing || '',
    floor: specs.floor || '',
    totalFloors: specs.total_floors || '',
    yearBuilt: specs.year_built || specs.age || '',
    parking: specs.parking || '',
    amenities: amenities.map((a: any) => (typeof a === 'string' ? a : a?.name || '')).filter(Boolean),
    ownerName: listing.owner_name || listing.user_profiles?.full_name || 'Owner',
    ownerMobile: listing.owner_mobile || listing.user_profiles?.mobile || '',
    ownerWhatsapp: listing.owner_whatsapp || listing.owner_mobile || '',
    ownerAvatar: listing.user_profiles?.avatar_url || null,
    ownerVerified: listing.user_profiles?.is_verified || listing.is_verified,
    khataStatus: listing.khata_status || '',
    ecAvailable: listing.ec_available,
    loanEligible: listing.loan_eligible,
    specs,
    createdAt: listing.created_at,
  };
}

// ─── API Functions ───────────────────────────────────────────────────────────

export async function getFeaturedListings(limit = 8): Promise<ListingRow[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('listings')
    .select('*, categories(name, slug, icon, color), user_profiles!listings_owner_user_id_fkey(full_name, avatar_url, mobile, is_verified)')
    .eq('listing_status', 'active')
    .eq('approval_status', 'approved')
    .eq('is_featured', true)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('getFeaturedListings error:', error.message);
    return [];
  }
  return (data || []) as ListingRow[];
}

export async function getActiveListings(filters?: {
  category?: string;
  categoryId?: string;
  subcategoryId?: string;
  minPrice?: number;
  maxPrice?: number;
  city?: string;
  search?: string;
  sortBy?: string;
  limit?: number;
  offset?: number;
}): Promise<{ listings: ListingRow[]; count: number }> {
  const supabase = createClient();
  const limit = filters?.limit || 20;
  const offset = filters?.offset || 0;

  let query = supabase
    .from('listings')
    .select('*, categories(name, slug, icon, color), user_profiles!listings_owner_user_id_fkey(full_name, avatar_url, mobile, is_verified)', { count: 'exact' })
    .eq('listing_status', 'active')
    .eq('approval_status', 'approved');

  if (filters?.categoryId) {
    query = query.eq('category_id', filters.categoryId);
  } else if (filters?.subcategoryId) {
    query = query.eq('category_id', filters.subcategoryId);
  }

  if (filters?.city) {
    query = query.ilike('city', `%${filters.city}%`);
  }
  if (filters?.minPrice) {
    query = query.gte('price', filters.minPrice);
  }
  if (filters?.maxPrice) {
    query = query.lte('price', filters.maxPrice);
  }
  if (filters?.search) {
    query = query.ilike('title', `%${filters.search}%`);
  }

  // Sort
  if (filters?.sortBy === 'price-asc') {
    query = query.order('price', { ascending: true });
  } else if (filters?.sortBy === 'price-desc') {
    query = query.order('price', { ascending: false });
  } else if (filters?.sortBy === 'popular') {
    query = query.order('is_featured', { ascending: false }).order('created_at', { ascending: false });
  } else {
    query = query.order('created_at', { ascending: false });
  }

  query = query.range(offset, offset + limit - 1);

  const { data, error, count } = await query;

  if (error) {
    console.error('getActiveListings error:', error.message);
    return { listings: [], count: 0 };
  }
  return { listings: (data || []) as ListingRow[], count: count || 0 };
}

export async function getListingById(id: string): Promise<ListingRow | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('listings')
    .select('*, categories(name, slug, icon, color), user_profiles!listings_owner_user_id_fkey(full_name, avatar_url, mobile, is_verified)')
    .eq('id', id)
    .eq('listing_status', 'active')
    .maybeSingle();

  if (error) {
    console.error('getListingById error:', error.message);
    return null;
  }
  return data as ListingRow | null;
}

export async function getSimilarListings(listingId: string, categoryId: string | null, limit = 3): Promise<ListingRow[]> {
  const supabase = createClient();
  let query = supabase
    .from('listings')
    .select('*, categories(name, slug, icon, color)')
    .eq('listing_status', 'active')
    .eq('approval_status', 'approved')
    .neq('id', listingId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (categoryId) {
    query = query.eq('category_id', categoryId);
  }

  const { data, error } = await query;
  if (error) {
    console.error('getSimilarListings error:', error.message);
    return [];
  }
  return (data || []) as ListingRow[];
}

export async function getListingsStats(): Promise<{ totalListings: number; totalCities: number; totalSellers: number }> {
  const supabase = createClient();
  const { count } = await supabase
    .from('listings')
    .select('*', { count: 'exact', head: true })
    .eq('listing_status', 'active')
    .eq('approval_status', 'approved');

  return {
    totalListings: count || 0,
    totalCities: 12,
    totalSellers: Math.floor((count || 0) * 0.7),
  };
}
